import { useCallback } from 'react';
import {
  formatAddress,
  formatMobileTableData,
  IMobileTableScema,
} from '../../../helpers/formatters';
import TableCardMobile from '../../../molecules/Table/mobile/TableCard';
import StatsMobile, { IStatsMobileProps } from '../mobile/Stats';
import { formatTimestamp, formatTx } from '../formatters';
import Loader from '../../common/Loader';
import { onTxScan, onWalletScan } from '../../../helpers/addressUtils';
import { TableTrade } from '../../../core/models/table-trade.model';
import NumberDisplay from '../../../molecules/NumberDisplay';
import TableHeaderMobile from '../../../molecules/Table/mobile/TableHeader';

interface ITradesTableMobileProps {
  trades: TableTrade[];
  timezone?: string;
  base: string;
  quote: string;
  flipped: boolean;
  address: string | undefined;
  chain: string;
  loading: boolean;
  date?: number;
}

const TradesTableMobile = ({
  trades,
  timezone,
  base,
  quote,
  flipped,
  address,
  chain,
  loading,
  date,
}: ITradesTableMobileProps) => {
  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        name: 'Tx Value ($)',
        accessor: 'txValue',
        parser: (value) => <NumberDisplay value={value} string />,
        width: 85,
        string: true,
      },
      item_2: {
        name: 'Price ($)',
        accessor: 'priceUSD',
        parser: (value) => <NumberDisplay value={value} string />,
        width: 85,
        string: true,
      },

      item_3: {
        name: quote,
        accessor: 'price',
        parser: (value) => (
          <NumberDisplay value={value} startSymbol='(' endSymbol=')' string />
        ),
        string: true,
      },
    },
    row_2: {
      item_1: {
        name: 'Date',
        accessor: 'timestamp',
        parser: (value) => formatTimestamp(value, timezone),
        width: 130,
      },
      item_2: {
        name: 'Amount',
        accessor: 'numericIn',
        parser: (value) => (
          <NumberDisplay value={value} endSymbol={` ${base}`} string />
        ),
        string: true,
      },
    },
    row_3: {
      item_1: {
        name: 'Total',
        accessor: 'numericOut',
        parser: (value) => (
          <NumberDisplay value={value} endSymbol={` ${quote}`} string />
        ),
        width: 130,
        string: true,
      },
      item_2: {
        name: 'Address',
        accessor: 'wallet',
        parser: (value) => formatAddress(value),
        onClick: (row) => onWalletScan(row.wallet, chain),
        color: 'navy',
      },
    },
    row_4: {
      item_1: {
        name: 'Tx',
        accessor: 'tx',
        parser: (value) => formatTx(value),
        onClick: (row) => onTxScan(row.tx, chain),
        color: 'navy',
      },
    },
  };

  const renderFooter = (props: any) => <StatsMobile {...props} />;
  const footerData: Omit<IStatsMobileProps, 'data'> = {
    address,
    chain,
    date,
    timezone,
  };

  /**
   * acknowledge flipped when displaying buy/sell
   */
  const getColorForItem = useCallback(
    (row: any) => {
      if (row.side === 'Buy') {
        return flipped ? 'red' : 'green';
      } else {
        return flipped ? 'green' : 'red';
      }
    },
    [flipped]
  );

  return (
    <div className='dex_trades_mobile__table'>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !trades.length ? (
        <div className='centrify'>No data found</div>
      ) : (
        <>
          <TableHeaderMobile schema={schema} />
          {formatMobileTableData({
            data: trades,
            schema,
            getColorForItem,
            renderFooter,
            footerData,
            keyName: ['uuid'],
          })?.map((row) => (
            <TableCardMobile key={row.key} data={row} />
          ))}
        </>
      )}
    </div>
  );
};

export default TradesTableMobile;
